import { FolderKanban, LayoutGrid, ListChecks } from "lucide-react";
import * as React from "react";
import { Link } from "react-router-dom";

import { portalLaunchUrl, useApps } from "@/api/apps";
import { useSession } from "@/api/auth";
import {
  type Project,
  type ProjectListResponse,
  type ProjectRun,
  useProjectList,
  wholeProjectList,
} from "@/api/projects";
import { useRuns } from "@/api/runs";
import { LogoWell } from "@/components/apps/LogoWell";
import { relativeTime } from "@/components/grid/cells";
import { EmptyState } from "@/components/layout/EmptyState";
import { PageHeader } from "@/components/layout/PageHeader";
import { elapsedLabel } from "@/components/runs/run-header-meta";
import { RunStateBadge } from "@/components/runs/run-state-badge";
import { absentTimingLabel } from "@/components/runs/run-timing";
import { buttonVariants } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { apiErrorMessage, classifyApiError } from "@/lib/api-error-kind";
import { cn } from "@/lib/utils";

/**
 * The dashboard (flowstack#945, laid out to the flowstack#1976 design) —
 * the landing surface, answering "what is happening with my work right
 * now?": a header with the two submit actions, a summary strip, the
 * latest runs as a table, and the ATTMOS Suite apps as logos.
 *
 * Three rules shape everything below.
 *
 * **It summarises; it never computes.** Every number here is a field
 * some endpoint already returns, or a rollup over a list the response
 * PROVES is complete (`/api/projects` with its `total`, flowstack#1866).
 * Nothing is assembled by paging the browser through a bounded feed — a
 * total derived that way is slow and quietly wrong the moment the data
 * outgrows the page size. Where a KPI had no source, it is ABSENT and
 * filed, not faked:
 *
 * - **completed / failed in the last 7 days** — the design's two other
 *   strip tiles. No endpoint returns a windowed count, and `/api/runs`
 *   is one capped page, so neither is rendered.
 * - **who submitted a run** — the `runs` table has no user column at
 *   all, and both run routes synthesize `submitted_by` from the
 *   *requesting* session, so every row claims to be yours (#960).
 *   The table therefore carries no "By" column, and this page is scoped
 *   and worded as your TENANT's work, not "your" work.
 * - **artifact counts** — `/api/projects` reports a real
 *   `artifact_count` (#961), but it is the tenant's whole catalog while
 *   projects are single (#515), so it is not summed here.
 *
 * **It is a summary, not a second source of truth.** Every panel links
 * to the real page and every row links to its own detail route. Nothing
 * is reachable ONLY from here.
 *
 * **Each panel fails alone.** The three reads are independent queries
 * rendered through independent state machines (loading / error / empty
 * / content), so a 500 on one endpoint degrades one card instead of
 * blanking the landing page. Errors use the same four-way
 * classification the Analysis surface uses (`classifyApiError`,
 * flowstack#923) rather than one generic "something went wrong".
 *
 * Scoping is the session's tenant, enforced server-side by
 * `SessionDep` → `tenant_context` on every endpoint read here — the
 * same path the Analysis surface uses. `tests/server/
 * test_dashboard_scoping.py` pins the negative case.
 */
export function DashboardPage(): JSX.Element {
  const { data: session } = useSession();
  const projectList = useProjectList();
  const current = currentProject(projectList.data?.projects);

  const where = [session?.tenant_display_name, current?.display_name].filter(Boolean).join(" · ");

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Dashboard"
        description={where ? `Work in ${where}.` : "Your runs, projects and apps."}
        actions={current ? <SubmitActions project={current} /> : undefined}
      />
      <SummaryStrip
        data={projectList.data}
        isLoading={projectList.isLoading}
        isError={projectList.isError}
        error={projectList.error}
      />
      <div className="grid gap-5 lg:grid-cols-3">
        <LatestWorkPanel className="lg:col-span-2" />
        <SuitePanel />
      </div>
    </div>
  );
}

/**
 * The project the header's submit actions target: the one with the most
 * work in flight, then by name — the same order the Projects panel this
 * replaced listed them in. On a pilot tenant there is exactly one.
 */
function currentProject(projects: Project[] | undefined): Project | undefined {
  if (!projects || projects.length === 0) return undefined;
  return [...projects].sort(
    (a, b) =>
      b.active_run_count - a.active_run_count || a.display_name.localeCompare(b.display_name)
  )[0];
}

/** The two primary actions, as LINKS styled as buttons (fs#1797: never a
 * `<button>` inside an `<a>`). `lg` is the 44px target. */
function SubmitActions({ project }: { project: Project }): JSX.Element {
  const base = `/projects/${encodeURIComponent(project.slug)}/submit`;
  return (
    <>
      <Link to={`${base}/flow`} className={buttonVariants({ size: "lg", className: "px-5" })}>
        Submit a flow
      </Link>
      <Link
        to={`${base}/tile`}
        className={buttonVariants({ variant: "outline", size: "lg", className: "px-5" })}
      >
        Submit a tile
      </Link>
    </>
  );
}

// ---- Panel chrome ---------------------------------------------------

interface PanelProps {
  title: string;
  /** Where "the real page" is — the panel header links here. */
  to: string;
  /** Link text; names the destination so it is unambiguous out of context. */
  linkLabel: string;
  className?: string;
  children: React.ReactNode;
}

/**
 * One dashboard card. The header's link is what keeps this a summary
 * rather than a second source of truth.
 *
 * Each panel is a labelled `region` landmark, not a bare card: the
 * dashboard is several independent things on one screen, so a user
 * navigating by landmark can reach "Latest work" directly. The label
 * comes from the panel's own heading via `aria-labelledby`, so the two
 * can never drift apart.
 */
function Panel({ title, to, linkLabel, className, children }: PanelProps): JSX.Element {
  const headingId = React.useId();
  return (
    <Card
      elevation="raised"
      role="region"
      aria-labelledby={headingId}
      className={cn("flex min-w-0 flex-col gap-4 p-card", className)}
    >
      <div className="flex items-baseline justify-between gap-3">
        <h2 id={headingId} className="label-micro font-semibold text-muted-foreground">
          {title}
        </h2>
        <Link
          to={to}
          className="text-xs font-medium text-accent underline-offset-4 hover:underline"
        >
          {linkLabel}
        </Link>
      </div>
      {children}
    </Card>
  );
}

function PanelSkeleton(): JSX.Element {
  return (
    <div className="flex flex-col gap-2">
      {Array.from({ length: 3 }).map((_, i) => (
        <Skeleton key={i} className="h-10 w-full" />
      ))}
    </div>
  );
}

/**
 * Four-way failure copy per panel (#923). `subject` names what could
 * not be read, so "we couldn't check" never reads as "there is
 * nothing here" — the two are different claims and only one of them
 * is the user's problem to act on.
 */
function describePanelError(
  error: unknown,
  subject: string
): { title: string; description: string } {
  const detail = apiErrorMessage(error);
  switch (classifyApiError(error)) {
    case "network":
      return {
        title: "Could not reach the server.",
        description: "Check your connection and try again.",
      };
    case "forbidden":
      return { title: `You don't have permission to view ${subject}.`, description: detail };
    case "not-found":
      return { title: `${subject} could not be found.`, description: detail };
    case "server":
      return { title: `The server failed to load ${subject}.`, description: detail };
    default:
      return { title: `Could not load ${subject}.`, description: detail };
  }
}

function PanelError({ error, subject }: { error: unknown; subject: string }): JSX.Element {
  const { title, description } = describePanelError(error, subject);
  return <EmptyState className="py-8" title={title} description={description} />;
}

// ---- Summary strip --------------------------------------------------

interface SummaryStripProps {
  data: ProjectListResponse | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
}

/**
 * Runs in flight, all runs, and projects with work in flight — each a sum
 * of the per-project counts `/api/projects` computes server-side
 * (`active_run_count`, `run_count`), taken only over a list the response
 * proves whole (`wholeProjectList`). The runs feed is never counted: it is
 * one capped page.
 *
 * The design's other two tiles — completed and failed in the last 7 days —
 * have no source and are absent (see the page comment). This strip also
 * carries what the old "Projects running" panel said.
 */
function SummaryStrip({ data, isLoading, isError, error }: SummaryStripProps): JSX.Element {
  const headingId = React.useId();
  let body: React.ReactNode;

  if (isLoading) {
    body = (
      <div className="grid gap-4 sm:grid-cols-3">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-24" />
        ))}
      </div>
    );
  } else if (isError || !data) {
    body = (
      <Card>
        <PanelError error={error} subject="your projects" />
      </Card>
    );
  } else if (data.projects.length === 0) {
    body = (
      <Card>
        <EmptyState
          className="py-8"
          icon={<FolderKanban className="h-7 w-7" aria-hidden />}
          title="No projects yet."
          description="A project is where runs and their artifacts live. Pilot tenants are seeded with one; contact your FlowStack administrator if you don't see it."
          cta={
            <Link
              to="/projects"
              className="text-sm font-medium text-accent underline-offset-4 hover:underline"
            >
              Go to Projects
            </Link>
          }
        />
      </Card>
    );
  } else {
    const projects = wholeProjectList(data);
    body =
      projects === undefined ? (
        <Card className="p-card">
          <p className="text-sm text-muted-foreground">
            Run totals are unavailable: this server did not confirm the project list is complete.{" "}
            <Link
              to="/projects"
              className="font-medium text-accent underline-offset-4 hover:underline"
            >
              All projects
            </Link>
          </p>
        </Card>
      ) : (
        <SummaryTiles projects={projects} />
      );
  }

  return (
    <section aria-labelledby={headingId} className="flex flex-col gap-2">
      <h2 id={headingId} className="sr-only">
        Summary
      </h2>
      {body}
    </section>
  );
}

function SummaryTiles({ projects }: { projects: Project[] }): JSX.Element {
  const inFlight = projects.reduce((sum, p) => sum + p.active_run_count, 0);
  const allRuns = projects.reduce((sum, p) => sum + p.run_count, 0);
  const running = projects.filter((p) => p.active_run_count > 0).length;

  return (
    <div className="grid gap-4 sm:grid-cols-3">
      <SummaryTile label="In flight now" value={inFlight} />
      <SummaryTile label="All runs" value={allRuns} />
      <SummaryTile
        label="Projects running"
        value={running}
        detail={
          <>
            <span>{`of ${countLabel(projects.length, "project")}`}</span>
            <Link
              to="/projects"
              className="font-medium text-accent underline-offset-4 hover:underline"
            >
              All projects
            </Link>
          </>
        }
      />
    </div>
  );
}

/** One strip tile: a labelled `group`, so the number is read with its label. */
function SummaryTile({
  label,
  value,
  detail,
}: {
  label: string;
  value: number;
  detail?: React.ReactNode;
}): JSX.Element {
  const labelId = React.useId();
  return (
    <Card
      elevation="raised"
      role="group"
      aria-labelledby={labelId}
      className="flex flex-col gap-1 p-card"
    >
      <span id={labelId} className="label-micro text-muted-foreground">
        {label}
      </span>
      <span className="font-display text-3xl font-semibold tabular-nums text-foreground">
        {value}
      </span>
      {detail ? (
        <span className="flex items-baseline justify-between gap-2 text-xs text-muted-foreground">
          {detail}
        </span>
      ) : null}
    </Card>
  );
}

// ---- ATTMOS Suite ---------------------------------------------------

/**
 * The portals this session is entitled to open, as a logo grid.
 * `/api/apps` returns the entitled subset (entitlement is decided
 * server-side from the Cognito groups).
 *
 * Each logo opens its portal in a new tab exactly as the launcher does:
 * `portalLaunchUrl`'s client-side domain gate (an app that fails it is
 * dropped, not linked), `target="_blank"` and `noopener noreferrer`. The
 * logo sits in the same `LogoWell` the launcher uses, so one portal never
 * picks a different theme variant on the two pages.
 */
function SuitePanel(): JSX.Element {
  const { data: apps, isLoading, isError, error } = useApps();
  const launchable = (apps ?? []).flatMap((app) => {
    const url = portalLaunchUrl(app);
    return url === null ? [] : [{ app, url }];
  });

  return (
    <Panel title="ATTMOS Suite" to="/apps" linkLabel="All apps">
      {isLoading ? (
        <PanelSkeleton />
      ) : isError ? (
        <PanelError error={error} subject="your apps" />
      ) : launchable.length === 0 ? (
        <EmptyState
          className="py-8"
          icon={<LayoutGrid className="h-7 w-7" aria-hidden />}
          title="No apps available to you yet."
          description="Ask a FlowStack admin for access, or open the launcher to request it from the owning team."
          cta={
            <Link
              to="/apps"
              className="text-sm font-medium text-accent underline-offset-4 hover:underline"
            >
              Open the launcher
            </Link>
          }
        />
      ) : (
        <ul className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-2">
          {launchable.map(({ app, url }) => (
            <li key={app.slug}>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={`Open ${app.display_name} in a new tab`}
                className={cn(
                  "group flex min-h-11 flex-col items-center gap-2 rounded-lg p-2 outline-none",
                  "transition-colors hover:bg-surface-2",
                  "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                )}
              >
                <LogoWell icon={app.icon} size="sm" />
                <span className="w-full truncate text-center text-xs font-medium text-foreground">
                  {app.display_name}
                </span>
              </a>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

// ---- Latest work ----------------------------------------------------

/** How many recent runs the summary shows before deferring to /runs. */
const LATEST_WORK_LIMIT = 6;

/**
 * The most recent runs, newest first, as a table. `/api/runs` is already
 * ordered most-recent-first server-side, so this slices rather than sorts.
 *
 * **No count is derived from this feed.** `/api/runs` is bounded
 * (`limit`, default 200), so "N runs in flight" computed here would be
 * silently capped — right on a pilot tenant, wrong on a busy one, and
 * wrong in the direction that under-reports. The totals live in the
 * summary strip, which reads counts the server computed.
 *
 * **Duration is printed only where the run record has both ends**
 * (`elapsedLabel`, the run header's own helper). A settled run without a
 * window says "not recorded" and an in-flight one keeps the dash —
 * `run-timing.ts` owns that wording, because a blank there once read as
 * "served from cache" across 300 runs.
 *
 * No polling loop is added here. `useRuns` carries a 10s `staleTime`
 * and TanStack refetches on window focus; a dashboard that opened its
 * own interval would be a second poller against the same endpoint the
 * runs page already polls.
 */
function LatestWorkPanel({ className }: { className?: string }): JSX.Element {
  const { data: runs, isLoading, isError, error } = useRuns();
  const latest = runs ? runs.slice(0, LATEST_WORK_LIMIT) : [];

  return (
    <Panel title="Latest work" to="/runs" linkLabel="All runs" className={className}>
      {isLoading ? (
        <PanelSkeleton />
      ) : isError ? (
        <PanelError error={error} subject="your latest runs" />
      ) : latest.length === 0 ? (
        <EmptyState
          className="py-8"
          icon={<ListChecks className="h-7 w-7" aria-hidden />}
          title="No runs yet."
          description="Submit a Tile or a Flow from a project — or from the SDK — and it will appear here as it progresses."
          cta={
            <Link
              to="/projects"
              className="text-sm font-medium text-accent underline-offset-4 hover:underline"
            >
              Pick a project to submit from
            </Link>
          }
        />
      ) : (
        <div className="flex min-w-0 flex-col gap-3">
          {/* States the truncation instead of implying these are all
              the runs there are. A bare list of six reads as "you have
              six runs" to anyone who does not already know better. */}
          <p className="text-sm text-muted-foreground">
            {`Your ${countLabel(latest.length, "most recent run")}.`}
          </p>
          {/* The table scrolls inside its own box at phone width rather
              than widening the page. */}
          <div className="overflow-x-auto">
            <Table className="min-w-[36rem]">
              <TableCaption>Latest work</TableCaption>
              <TableHeader>
                <TableRow>
                  <TableHead>Flow / tile</TableHead>
                  <TableHead>Project</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Submitted</TableHead>
                  <TableHead align="right">Duration</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {latest.map((run) => (
                  <RunRow key={run.run_id} run={run} />
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </Panel>
  );
}

/**
 * One run row. The status is the signature `RunStateBadge` — the same
 * component the runs grid and the DAG nodes render — so a state never
 * looks like one thing here and another on the page this links to, and
 * a failure names its class (`failed scientific`, `failed system`, …).
 */
function RunRow({ run }: { run: ProjectRun }): JSX.Element {
  const workload = run.flow_name ?? run.tile_name ?? run.run_id;
  return (
    <TableRow interactive="hover">
      <TableCell className="max-w-[16rem]">
        <Link
          to={`/runs/${encodeURIComponent(run.run_id)}`}
          className="inline-flex min-h-11 max-w-full items-center truncate font-medium text-foreground underline-offset-4 hover:underline"
        >
          {workload}
        </Link>
      </TableCell>
      <TableCell className="text-muted-foreground">{run.project_slug ?? "no project"}</TableCell>
      <TableCell>
        <RunStateBadge state={run.status} />
      </TableCell>
      <TableCell className="whitespace-nowrap text-muted-foreground">
        <RelativeSubmitted at={run.submitted_at} />
      </TableCell>
      <TableCell align="right" className="whitespace-nowrap tabular-nums text-muted-foreground">
        {elapsedLabel(run) ?? absentTimingLabel(run.status)}
      </TableCell>
    </TableRow>
  );
}

/** "2m ago", with the absolute timestamp on hover. */
function RelativeSubmitted({ at }: { at: string }): JSX.Element {
  const when = new Date(at);
  return (
    <time dateTime={at} title={when.toLocaleString()}>
      {relativeTime(when)}
    </time>
  );
}

/** "1 run" / "3 runs" — pluralisation without a dependency. */
function countLabel(count: number, noun: string): string {
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}
